import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Oshawa = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    const introLink = isChinese
      ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
      : 'https://liv.rent/blog/neighbourhoods/neighbourhood-guide-downtown-toronto/'

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.oshawa:About Oshawa Paragraph 1">
            Boasting strong employment rates and affordable rental housing,
            Oshawa emerges as an attractive alternative for{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>{' '}
            renters. Situated approximately 60km east of{' '}
            <LinkText href={introLink}>Downtown Toronto</LinkText> on Lake
            Ontario, Oshawa marks the eastern boundaries of the{' '}
            <LinkText href={cheapestLink}>Greater Toronto Area (GTA)</LinkText>,
            Ontario, offering a relaxed community with cultural richness, parks,
            trails, and local amenities.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.oshawa:About Oshawa Paragraph 2">
            For those seeking an escape from the urban bustle while maintaining
            convenience, Oshawa strikes an ideal equilibrium. It features a
            leading hospital, top-notch schools, and a robust transit network
            facilitating access to the surrounding region, including the{' '}
            <LinkText href="/rental-listings/city/oshawa/ontario-tech-university">
              ON Tech University
            </LinkText>
            . With comparatively modest costs, abundant rental options from
            apartments to townhouses, Oshawa remains a sought-after destination,
            catering to tenants of varying preferences within Ontario’s vibrant
            landscape.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,300 - $2,200',
          bed2: '$1,500 - $2,500',
          bed3: '$2,000 - $3,000'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.oshawa:Apartment Hunting in Oshawa Paragraph 1">
            Oshawa is an attractive city of Ontario known for its thriving
            community and diverse housing options. While rental prices in Oshawa
            are generally more affordable compared to Toronto, they have been
            steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.under1000}>cheap rentals in Oshawa</LinkText>,
            especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , can be challenging. However, to initiate your search, you can
            refer to this guide highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>{' '}
            that may offer relatively more budget-friendly rental options.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.oshawa:Apartments for rent Oshawa Paragraph">
              There are several apartment complexes in Oshawa, catering
              primarily to renters. These range from older, low-rise buildings
              to newer, more modern structures. Compared to other Canadian
              cities, renters should see a slightly lower price for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in Oshawa
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Houses for rent CITY Paragraph 2">
              There are plenty of{' '}
              <LinkText href={links.house}>
                houses for rent in {{ cityName }}
              </LinkText>{' '}
              for those with a higher budget, or who have multiple roommates. In
              more residential areas of the city, both partial houses and entire
              houses for rent are common – as well as both new builds and older
              homes from the city’s earlier days. {{ cityName }} is also known
              for an abundance of laneway homes and townhomes within the city,
              making it easy to find a place to rent that suits your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.oshawa:Townhouses for rent Oshawa Paragraph">
              Townhouses are becoming more prevalent in Oshawa as it combines
              the best qualities of both apartments and whole houses. Typically
              less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in Oshawa
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.oshawa:Condos for rent Oshawa Paragraph">
              In more urban areas like Downtown Oshawa,{' '}
              <LinkText href={links.apartment}>
                condos for rent in Oshawa
              </LinkText>{' '}
              are more commonly seen. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Short term rentals LOCATION Paragraph">
              {{ cityName }} has plenty of work opportunities and excellent
              academic institutions, making it a popular place for those renting
              on a short-term basis.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> are
              exceedingly popular in most cities, particularly during the summer
              months and at the beginning of school semesters. The added
              flexibility is an appealing prospect for many renters, and a key
              factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.oshawa:Living in Oshawa Paragraph">
          Living in Oshawa,{' '}
          <LinkText href="/rental-listings/ontario">Ontario</LinkText>, offers
          residents a blend of urban amenities and suburban tranquility. As a
          growing city within the Durham Region, Oshawa has evolved from its
          historical roots in the automotive industry to become a vibrant
          educational hub, thanks to institutions like{' '}
          <LinkText href="/rental-listings/city/oshawa/ontario-tech-university">
            Ontario Tech University
          </LinkText>{' '}
          and Durham College. This transformation makes Oshawa suitable for a
          diverse population, from students and young professionals to families
          and retirees. While the city has traditionally been more
          homeowner-oriented, recent developments and an increase in rental
          accommodations, including apartments and basement suites, make it
          increasingly renter-friendly.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.oshawa:Popular Neighbourhoods Oshawa Paragraph">
          Like any city, Oshawa is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Oshawa, O’Neill, Lakeview, Centennial, Taunton, Pinecrest,
          and Windfields. For more info about Oshawa, you can consult this{' '}
          <LinkText href={neighborhoodsLink}>
            guide on Toronto neighbourhoods
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.oshawa:Local Landmarks Oshawa Paragraph">
          Oshawa’s rich history in the automotive industry makes it a
          destination for Canadian car lovers. The city is home to General
          Motors Canadian headquarters, as well as the Canadian Automotive
          Museum. The Parkwood National Historic Site highlights the lifestyles
          of the 1920s and 30s, and its stunning grounds are a great place to
          spend an afternoon.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Oshawa
